import React, { useEffect, useCallback, useRef, useState } from 'react';
import { VibesitesImage } from '@vibesites/ui-primitives';

/**
 * Shared building blocks for the photo gallery (mosaic grid and lightbox).
 * Not a registered component — has no `contentSchema`, never appears in
 * `registry.ts`, and is not re-exported from `components/index.ts`.
 *
 * The schema linter skips files without a contentSchema, so this helper
 * sits safely alongside the registered gallery sections without tripping
 * any rules (every photo is rendered through VibesitesImage).
 */

export interface GalleryImage {
  image: string;
  alt_text?: string;
  title?: string;
  description?: string;
  source_page_urls?: string;
  original_url?: string;
}

/** Classify a gallery photo by its title — used to split community vs unit shots. */
export function classifyPhoto(photo: GalleryImage): 'apartment' | 'community' | 'other' {
  const title = (photo.title || '').toLowerCase().trim();
  if (title.includes('apartment')) return 'apartment';
  if (title.includes('community')) return 'community';
  return 'other';
}

/** True when photo_gallery.description is Featured (case-insensitive). */
export function isFeaturedDescription(description?: string | null): boolean {
  return (description || '').trim().toLowerCase() === 'featured';
}

/**
 * Prefer Featured photos when any exist; otherwise use the full filtered set.
 * Caps at `limit` (home section carousels).
 */
export function pickGallerySlides<T extends {
  image?: string | null;
  original_url?: string | null;
  description?: string | null;
}>(photos: T[], limit = 5): T[] {
  const withImg = photos.filter((p) => Boolean(p.image || p.original_url));
  const featured = withImg.filter((p) => isFeaturedDescription(p.description));
  return (featured.length ? featured : withImg).slice(0, limit);
}

/** Crossfade carousel for home section media frames. */
export function GalleryFadeCarousel({
  images,
  className,
  imgClassName,
  sizes,
  intervalMs = 5000,
  emptyClassName,
}: {
  images: GalleryImage[];
  className?: string;
  imgClassName?: string;
  sizes: string;
  intervalMs?: number;
  emptyClassName?: string;
}) {
  const slides = images
    .map((photo) => ({
      src: photo.image || photo.original_url || '',
      alt: photo.alt_text || photo.title || '',
    }))
    .filter((s) => s.src);

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setIndex(0);
  }, [slides.length, slides[0]?.src]);

  useEffect(() => {
    if (slides.length < 2 || paused) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [slides.length, paused, intervalMs]);

  if (!slides.length) {
    return <div className={emptyClassName || imgClassName} aria-hidden="true" />;
  }

  return (
    <div
      className={className}
      style={{ position: 'relative', width: '100%', height: '100%' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {slides.map((slide, i) => (
        <div
          key={`${slide.src}-${i}`}
          aria-hidden={i !== index}
          style={{
            position: 'absolute',
            inset: 0,
            opacity: i === index ? 1 : 0,
            transition: 'opacity 0.8s ease',
          }}
        >
          <VibesitesImage
            url={slide.src}
            alt={i === index ? slide.alt : ''}
            sizes={sizes}
            fill
            objectFit="cover"
            className={imgClassName}
            priority={i === 0}
          />
        </div>
      ))}
      {slides.length > 1 ? (
        <div className="gallery-fade-dots" aria-hidden="true">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`gallery-fade-dot${i === index ? ' is-active' : ''}`}
              onClick={() => setIndex(i)}
              tabIndex={-1}
            />
          ))}
        </div>
      ) : null}
      <style>{`
        .gallery-fade-dots {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0.75rem;
          z-index: 2;
          display: flex;
          justify-content: center;
          gap: 0.4rem;
          pointer-events: none;
        }
        .gallery-fade-dot {
          pointer-events: auto;
          width: 0.45rem;
          height: 0.45rem;
          padding: 0;
          border: 0;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.45);
          cursor: pointer;
        }
        .gallery-fade-dot.is-active {
          background: rgba(255, 255, 255, 0.95);
        }
        @media (prefers-reduced-motion: reduce) {
          .gallery-fade-dots { display: none; }
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// Gallery page desirability ordering — most desirable first, least last
// ============================================================================
// Photos are grouped apartment → community → other, then ranked within each
// group by relevance tiers so like images stay together (all kitchens, all
// pools, etc.). Matching uses title / description / alt / URL text so it works
// across properties whose gallery metadata varies. Applied only on the
// gallery page (PhotoGallery).

/** Apartment / interior — most → least desirable. */
const APARTMENT_GALLERY_TIERS: readonly (readonly string[])[] = [
  ['kitchen', 'kitchenette', 'kitchen island'],
  ['dining room', 'dining area', 'dining space', 'dining'],
  [
    'living room',
    'living area',
    'living space',
    'great room',
    'family room',
    'den',
  ],
  ['bathroom', 'bath room', 'restroom', 'shower', 'vanity', 'powder room'],
  ['bedroom', 'bed room', 'master suite', 'primary suite', 'sleeping'],
  ['closet', 'walk-in', 'wardrobe', 'storage', 'pantry', 'linen'],
  ['hallway', 'hall way', 'corridor', 'entryway', 'foyer', 'entry hall'],
  ['patio', 'balcony', 'terrace', 'private outdoor', 'private deck', 'deck'],
];

/** Community — most → least desirable (exterior handled separately as last). */
const COMMUNITY_GALLERY_TIERS: readonly (readonly string[])[] = [
  [
    'clubhouse',
    'club house',
    'resident lounge',
    'community lounge',
    'social lounge',
    'leasing lounge',
  ],
  ['fitness', 'gym', 'workout', 'yoga', 'wellness', 'spa'],
  [
    'game room',
    'gaming',
    'esports',
    'theater',
    'cinema',
    'media room',
    'cowork',
    'co-work',
    'co working',
    'business center',
    'conference',
    'entertainment',
    'lounge',
  ],
  ['pool', 'poolside', 'swimming', 'cabana'],
  [
    'grill',
    'bbq',
    'barbecue',
    'courtyard',
    'fire pit',
    'firepit',
    'outdoor kitchen',
    'playground',
    'dog park',
    'pet park',
    'picnic',
    'gazebo',
    'rooftop',
    'sky deck',
    'outdoor seating',
    'seating area',
  ],
];

/** Exterior / property overview — always last within community. */
const EXTERIOR_GALLERY_KEYWORDS: readonly string[] = [
  'exterior',
  'building exterior',
  'exterior building',
  'building facade',
  'building façade',
  'facade',
  'façade',
  'aerial',
  'drone',
  'property overview',
  'street view',
  'curb appeal',
  'front elevation',
  'building front',
  'leasing office exterior',
];

const UNRANKED = Number.POSITIVE_INFINITY;

function getUrlPath(url: string | null | undefined): string {
  if (!url) return '';
  try {
    return decodeURIComponent(new URL(url).pathname);
  } catch {
    return url;
  }
}

/** Flatten searchable metadata for keyword matching. */
function galleryPhotoText(photo: GalleryImage): string {
  return [
    photo.title,
    photo.description,
    photo.alt_text,
    getUrlPath(photo.image),
    getUrlPath(photo.original_url),
    photo.source_page_urls,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function firstMatchingTier(
  text: string,
  tiers: readonly (readonly string[])[],
): number {
  if (!text) return UNRANKED;
  for (let i = 0; i < tiers.length; i++) {
    for (const keyword of tiers[i]) {
      if (text.includes(keyword)) return i;
    }
  }
  return UNRANKED;
}

function matchesKeywords(text: string, keywords: readonly string[]): boolean {
  if (!text) return false;
  return keywords.some((keyword) => text.includes(keyword));
}

function textMatchesAnyTier(
  text: string,
  tiers: readonly (readonly string[])[],
): boolean {
  return firstMatchingTier(text, tiers) !== UNRANKED;
}

/**
 * Resolve apartment vs community group for sorting. Prefers the photo title
 * classification, then falls back to keyword hits in the rest of the metadata.
 */
function gallerySortGroup(
  photo: GalleryImage,
  text: string,
): 'apartment' | 'community' | 'other' {
  const byTitle = classifyPhoto(photo);
  if (byTitle !== 'other') return byTitle;
  if (textMatchesAnyTier(text, APARTMENT_GALLERY_TIERS)) return 'apartment';
  if (
    textMatchesAnyTier(text, COMMUNITY_GALLERY_TIERS) ||
    matchesKeywords(text, EXTERIOR_GALLERY_KEYWORDS)
  ) {
    return 'community';
  }
  return 'other';
}

/**
 * Community tier: ranked amenities first, then unmatched, then exterior last.
 */
function communityGalleryTier(text: string): number {
  const ranked = firstMatchingTier(text, COMMUNITY_GALLERY_TIERS);
  if (ranked !== UNRANKED) return ranked;
  // Unmatched community sits after outdoor amenities, before exterior.
  if (matchesKeywords(text, EXTERIOR_GALLERY_KEYWORDS)) {
    return COMMUNITY_GALLERY_TIERS.length + 1;
  }
  return COMMUNITY_GALLERY_TIERS.length;
}

const GROUP_ORDER: Record<'apartment' | 'community' | 'other', number> = {
  apartment: 0,
  community: 1,
  other: 2,
};

/**
 * Sort gallery photos for the gallery page: apartment interiors first
 * (kitchens → dining → living → baths → beds → …), then community (clubhouse →
 * fitness → premium indoor → pools → outdoor → exterior), preserving
 * original order within the same tier.
 */
export function sortGalleryByDesirability(images: GalleryImage[]): GalleryImage[] {
  return images
    .map((img, index) => {
      const text = galleryPhotoText(img);
      const group = gallerySortGroup(img, text);
      let tier = UNRANKED;
      if (group === 'apartment') {
        tier = firstMatchingTier(text, APARTMENT_GALLERY_TIERS);
      } else if (group === 'community') {
        tier = communityGalleryTier(text);
      }
      return { img, index, groupOrder: GROUP_ORDER[group], tier };
    })
    .sort((a, b) => {
      if (a.groupOrder !== b.groupOrder) return a.groupOrder - b.groupOrder;
      if (a.tier !== b.tier) return a.tier - b.tier;
      return a.index - b.index;
    })
    .map((entry) => entry.img);
}

const MOSAIC_SIZE = 5;

function GalleryTile({
  img,
  index,
  onSelect,
  className,
  loading = 'lazy',
}: {
  img: GalleryImage;
  index: number;
  onSelect: (index: number) => void;
  className?: string;
  loading?: 'lazy' | 'eager';
}) {
  return (
    <div
      className={`gallery-item group cursor-pointer overflow-hidden ${className || ''}`}
      onClick={() => onSelect(index)}
    >
      <VibesitesImage
        url={img.image}
        alt={img.alt_text || img.title || ''}
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
        priority={loading === 'eager'}
      />
    </div>
  );
}

interface GalleryMosaicBlockProps {
  images: GalleryImage[];
  startIndex: number;
  tallOnRight: boolean;
  onSelect: (index: number) => void;
}

/** Five-up mosaic: 2×2 grid beside one tall portrait image. */
export function GalleryMosaicBlock({
  images,
  startIndex,
  tallOnRight,
  onSelect,
}: GalleryMosaicBlockProps) {
  if (images.length === 0) return null;

  if (images.length < MOSAIC_SIZE) {
    return (
      <div className="gallery-fallback-grid">
        {images.map((img, i) => (
          <GalleryTile
            key={img.image || startIndex + i}
            img={img}
            index={startIndex + i}
            onSelect={onSelect}
            className="gallery-fallback-grid__cell"
          />
        ))}
      </div>
    );
  }

  const quad = images.slice(0, 4);
  const tall = images[4];
  const modifier = tallOnRight ? 'gallery-mosaic--tall-right' : 'gallery-mosaic--tall-left';

  return (
    <div className={`gallery-mosaic ${modifier}`}>
      {quad.map((img, i) => (
        <GalleryTile
          key={img.image || startIndex + i}
          img={img}
          index={startIndex + i}
          onSelect={onSelect}
          className="gallery-mosaic__cell"
          loading={i === 0 && startIndex === 0 ? 'eager' : 'lazy'}
        />
      ))}
      <GalleryTile
        img={tall}
        index={startIndex + 4}
        onSelect={onSelect}
        className="gallery-mosaic__tall"
      />
    </div>
  );
}

/** Split a flat image list into repeating 5-up mosaic groups. */
export function chunkGalleryImages(images: GalleryImage[], size = MOSAIC_SIZE): GalleryImage[][] {
  const chunks: GalleryImage[][] = [];
  for (let i = 0; i < images.length; i += size) {
    chunks.push(images.slice(i, i + size));
  }
  return chunks;
}

interface GalleryLightboxProps {
  images: GalleryImage[];
  selectedIndex: number | null;
  onClose: () => void;
  onChange: (index: number | null) => void;
}

function LightboxChevron({ direction }: { direction: 'prev' | 'next' }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {direction === 'prev' ? (
        <path d="M15 18l-6-6 6-6" />
      ) : (
        <path d="M9 18l6-6-6-6" />
      )}
    </svg>
  );
}

export function GalleryLightbox({
  images,
  selectedIndex,
  onClose,
  onChange,
}: GalleryLightboxProps) {
  const total = images.length;

  const goNext = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      onChange(selectedIndex === null ? null : (selectedIndex + 1) % total);
    },
    [onChange, selectedIndex, total],
  );

  const goPrev = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      onChange(selectedIndex === null ? null : (selectedIndex - 1 + total) % total);
    },
    [onChange, selectedIndex, total],
  );

  useEffect(() => {
    if (selectedIndex === null) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedIndex]);

  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedIndex, goNext, goPrev, onClose]);

  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      const dy = e.changedTouches[0].clientY - touchStartY.current;
      touchStartX.current = null;
      touchStartY.current = null;

      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      if (dx < 0) goNext();
      else goPrev();
    },
    [goNext, goPrev],
  );

  const handleStageClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const stopClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const selectedImage = selectedIndex !== null ? images[selectedIndex] : null;
  if (!selectedImage) return null;

  const caption = selectedImage.description || selectedImage.alt_text || '';

  return (
    <div
      className="gallery-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo gallery viewer"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="gallery-lightbox__shell">
        <div className="gallery-lightbox__toolbar">
          {total > 1 ? (
            <p className="gallery-lightbox__counter" aria-live="polite">
              {(selectedIndex ?? 0) + 1} <span className="gallery-lightbox__counter-sep">/</span>{' '}
              {total}
            </p>
          ) : (
            <span aria-hidden="true" />
          )}

          <button
            type="button"
            onClick={onClose}
            className="gallery-lightbox__close"
            aria-label="Close gallery"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div
          className="gallery-lightbox__stage"
          onClick={handleStageClick}
        >
          {total > 1 && (
            <button
              type="button"
              onClick={goPrev}
              className="gallery-lightbox__nav gallery-lightbox__nav--prev gallery-lightbox__nav--desktop"
              aria-label="Previous photo"
            >
              <LightboxChevron direction="prev" />
            </button>
          )}

          <figure className="gallery-lightbox__figure">
            <div className="gallery-lightbox__img-wrap">
              <div className="gallery-lightbox__img-frame" onClick={stopClick}>
                <VibesitesImage
                  url={selectedImage.image}
                  alt={selectedImage.alt_text || selectedImage.title || 'Property photo'}
                  className="gallery-lightbox__img"
                />
              </div>
            </div>
            {caption ? (
              <figcaption className="gallery-lightbox__caption" onClick={stopClick}>
                {caption}
              </figcaption>
            ) : null}
          </figure>

          {total > 1 && (
            <button
              type="button"
              onClick={goNext}
              className="gallery-lightbox__nav gallery-lightbox__nav--next gallery-lightbox__nav--desktop"
              aria-label="Next photo"
            >
              <LightboxChevron direction="next" />
            </button>
          )}
        </div>

        {total > 1 && (
          <div className="gallery-lightbox__mobile-nav">
            <button
              type="button"
              onClick={goPrev}
              className="gallery-lightbox__nav gallery-lightbox__nav--prev gallery-lightbox__nav--mobile"
              aria-label="Previous photo"
            >
              <LightboxChevron direction="prev" />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="gallery-lightbox__nav gallery-lightbox__nav--next gallery-lightbox__nav--mobile"
              aria-label="Next photo"
            >
              <LightboxChevron direction="next" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Shared CSS for the gallery mosaic and lightbox.
 */
export function GallerySharedStyles() {
  return (
    <style>{`
      .gallery-constrained {
        width: 90vw;
        max-width: 90vw;
        margin-left: auto;
        margin-right: auto;
        padding-left: 0;
        padding-right: 0;
      }

      @media (max-width: 767px) {
        .gallery-constrained {
          width: 100%;
          max-width: 100%;
          padding-left: 1rem;
          padding-right: 1rem;
        }

        .gallery-mosaic-stack {
          gap: 0.375rem;
        }

        .gallery-mosaic {
          gap: 0.375rem;
        }

        .gallery-fallback-grid {
          gap: 0.375rem;
        }
      }

      .gallery-item {
        animation: gallery-fade-in 0.35s ease both;
      }

      @keyframes gallery-fade-in {
        from { opacity: 0; transform: scale(0.97); }
        to { opacity: 1; transform: scale(1); }
      }

      @media (prefers-reduced-motion: reduce) {
        .gallery-item { animation: none; }
      }

      /* ── Mosaic (mobile: single column; desktop: 2×2 + tall) ─ */
      .gallery-mosaic-stack {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      .gallery-mosaic {
        display: grid;
        gap: 0.75rem;
        grid-template-columns: 1fr;
        grid-auto-rows: auto;
      }

      .gallery-mosaic__cell {
        aspect-ratio: 4 / 3;
        grid-column: 1;
      }

      .gallery-mosaic__tall {
        grid-column: 1;
        aspect-ratio: 4 / 3;
      }

      @media (min-width: 768px) {
        .gallery-mosaic {
          grid-template-columns: 1fr 1fr;
          grid-template-rows: auto auto;
        }

        .gallery-mosaic--tall-right {
          grid-template-columns: 1fr 1fr minmax(0, 1fr);
        }

        .gallery-mosaic--tall-left {
          grid-template-columns: minmax(0, 1fr) 1fr 1fr;
        }

        .gallery-mosaic__cell {
          aspect-ratio: 4 / 3;
        }

        .gallery-mosaic__tall {
          grid-row: 1 / 3;
          aspect-ratio: auto;
        }

        .gallery-mosaic--tall-right .gallery-mosaic__cell:nth-child(1) { grid-column: 1; grid-row: 1; }
        .gallery-mosaic--tall-right .gallery-mosaic__cell:nth-child(2) { grid-column: 2; grid-row: 1; }
        .gallery-mosaic--tall-right .gallery-mosaic__cell:nth-child(3) { grid-column: 1; grid-row: 2; }
        .gallery-mosaic--tall-right .gallery-mosaic__cell:nth-child(4) { grid-column: 2; grid-row: 2; }
        .gallery-mosaic--tall-right .gallery-mosaic__tall { grid-column: 3; grid-row: 1 / 3; }

        .gallery-mosaic--tall-left .gallery-mosaic__tall { grid-column: 1; grid-row: 1 / 3; }
        .gallery-mosaic--tall-left .gallery-mosaic__cell:nth-child(1) { grid-column: 2; grid-row: 1; }
        .gallery-mosaic--tall-left .gallery-mosaic__cell:nth-child(2) { grid-column: 3; grid-row: 1; }
        .gallery-mosaic--tall-left .gallery-mosaic__cell:nth-child(3) { grid-column: 2; grid-row: 2; }
        .gallery-mosaic--tall-left .gallery-mosaic__cell:nth-child(4) { grid-column: 3; grid-row: 2; }
      }

      .gallery-fallback-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0.75rem;
      }

      .gallery-fallback-grid__cell {
        aspect-ratio: 4 / 3;
      }

      @media (min-width: 768px) {
        .gallery-fallback-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      /* ── Lightbox ───────────────────────────────────────────── */
      .gallery-lightbox {
        position: fixed;
        inset: 0;
        z-index: 50;
        display: flex;
        align-items: stretch;
        justify-content: center;
        background: rgba(0, 0, 0, 0.94);
        animation: gallery-lightbox-in 0.2s ease both;
      }

      @keyframes gallery-lightbox-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @media (prefers-reduced-motion: reduce) {
        .gallery-lightbox { animation: none; }
      }

      .gallery-lightbox__shell {
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        width: 100%;
        max-width: 100vw;
        height: 100dvh;
      }

      .gallery-lightbox__toolbar,
      .gallery-lightbox__stage,
      .gallery-lightbox__mobile-nav,
      .gallery-lightbox__nav,
      .gallery-lightbox__close,
      .gallery-lightbox__img-frame,
      .gallery-lightbox__caption {
        pointer-events: auto;
      }

      .gallery-lightbox__toolbar {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 0.875rem 1rem 0.5rem;
      }

      .gallery-lightbox__counter {
        margin: 0;
        font-family: var(--vibe-font-body);
        font-size: 0.875rem;
        font-weight: 500;
        letter-spacing: 0.06em;
        color: rgba(255, 255, 255, 0.88);
      }

      .gallery-lightbox__counter-sep {
        color: rgba(255, 255, 255, 0.45);
      }

      .gallery-lightbox__close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.5rem;
        height: 2.5rem;
        border: none;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
        cursor: pointer;
        transition: background 0.15s ease, transform 0.15s ease;
      }

      .gallery-lightbox__close:hover {
        background: rgba(255, 255, 255, 0.18);
      }

      .gallery-lightbox__close:active {
        transform: scale(0.96);
      }

      .gallery-lightbox__stage {
        flex: 1;
        min-height: 0;
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        align-items: center;
        gap: 0.5rem;
        padding: 0 0.5rem 1rem;
        cursor: pointer;
        touch-action: pan-y;
      }

      .gallery-lightbox__figure {
        margin: 0;
        min-width: 0;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.75rem;
        pointer-events: none;
      }

      .gallery-lightbox__img-wrap {
        flex: 1;
        min-height: 0;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
      }

      .gallery-lightbox__img-frame {
        display: inline-flex;
        max-width: 100%;
        max-height: 100%;
        cursor: default;
      }

      .gallery-lightbox__img {
        display: block;
        max-width: min(96vw, 1400px);
        max-height: calc(100dvh - 5.5rem);
        width: auto;
        height: auto;
        object-fit: contain;
        border-radius: 2px;
        box-shadow: 0 12px 48px rgba(0, 0, 0, 0.45);
      }

      .gallery-lightbox__caption {
        flex-shrink: 0;
        max-width: min(96vw, 1400px);
        margin: 0;
        padding: 0 0.5rem;
        font-family: var(--vibe-font-body);
        font-size: 0.8125rem;
        line-height: 1.4;
        letter-spacing: 0.02em;
        text-align: center;
        color: rgba(255, 255, 255, 0.72);
      }

      .gallery-lightbox__nav {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.75rem;
        height: 2.75rem;
        border: 1px solid rgba(255, 255, 255, 0.14);
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.08);
        color: #fff;
        cursor: pointer;
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
        -webkit-user-select: none;
        user-select: none;
      }

      .gallery-lightbox__nav:hover {
        background: rgba(255, 255, 255, 0.16);
        border-color: rgba(255, 255, 255, 0.28);
      }

      .gallery-lightbox__nav:active {
        transform: scale(0.96);
      }

      .gallery-lightbox__mobile-nav {
        display: none;
      }

      .gallery-lightbox__nav--desktop {
        cursor: pointer;
      }

      @media (min-width: 768px) {
        .gallery-lightbox__toolbar {
          padding: 1rem 1.25rem 0.625rem;
        }

        .gallery-lightbox__stage {
          gap: 1rem;
          padding: 0 1rem 1.25rem;
        }

        .gallery-lightbox__nav {
          width: 3rem;
          height: 3rem;
        }

        .gallery-lightbox__img {
          max-height: calc(100dvh - 6rem);
        }

        .gallery-lightbox__caption {
          font-size: 0.875rem;
        }
      }

      @media (max-width: 639px) {
        .gallery-lightbox__stage {
          grid-template-columns: minmax(0, 1fr);
          grid-template-rows: 1fr;
          gap: 0;
          padding: 0;
        }

        .gallery-lightbox__figure {
          width: 100%;
          padding: 0;
          align-items: center;
          justify-content: center;
        }

        .gallery-lightbox__img-wrap {
          width: 100%;
          align-items: center;
          justify-content: center;
        }

        .gallery-lightbox__img-frame {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          max-height: 100%;
        }

        .gallery-lightbox__nav--desktop {
          display: none;
        }

        .gallery-lightbox__mobile-nav {
          display: flex;
          flex-shrink: 0;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 0.5rem 1.25rem calc(0.875rem + env(safe-area-inset-bottom, 0px));
        }

        .gallery-lightbox__nav--mobile {
          width: 3.25rem;
          height: 3.25rem;
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.16);
        }

        .gallery-lightbox__nav--mobile:active {
          transform: scale(0.96);
        }

        .gallery-lightbox__img {
          display: block;
          width: 100%;
          max-width: 100%;
          height: auto;
          max-height: calc(100dvh - 8.5rem);
          margin: 0 auto;
          border-radius: 0;
          box-shadow: none;
          object-fit: contain;
        }

        .gallery-lightbox__caption {
          padding: 0 1rem 0.25rem;
          max-width: 100%;
        }
      }
    `}</style>
  );
}
